//changed due to package upgrade
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  MenuItem,
  Button,
  IconButton,
  Grid,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  alpha,
  useMediaQuery,
} from '@mui/material';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import {
  getSavedProgress,
  getAuthenticatedProgressUserId,
  saveProgress,
  clearTournamentWidgetReturnPath,
} from './handleState';
import AppInfoView from '@projectK/components/AppInfoView';
import AppGridContainer from '@projectK/components/AppGridContainer';
import jwtAxios from '@projectK/services/auth/JWT';
import { useInfoViewActionsContext } from '@projectK/context';
import { useDebounce } from './hooks/useDebounce';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import ReactQuill from 'react-quill-new';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import IntlMessages from '@projectK/helpers/IntlMessages';
import { HandleMobileFormat } from '@projectK/ReUseFunctions';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { encrypt } from '@projectK/modules/apps/encryption';

const buildValidationSchema = ({
  coachFullNameRequired = true,
  coachEmailRequired = true,
  coachPhoneRequired = true,
  maxTeamAllowed = 6,
  teamLabel = 'Team',
  organizationLabel = 'Organization',
  coachLabel = 'Coach',
} = {}) =>
  yup.object({
    organization_name: yup
      .string()
      .required(`${organizationLabel} name is required`),
    teams: yup
      .array()
      .of(
        yup.object({
          team_name: yup.string().required(`Please enter ${teamLabel} name`),
          coach_name: coachFullNameRequired
            ? yup.string().required(`${coachLabel} Name is required `).max(200)
            : yup.string().optional().nullable(),
          coach_email: coachEmailRequired
            ? yup
                .string()
                .required(`Please enter ${coachLabel} email address.`)
                .max(100)
                .matches(
                  /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                  () => <IntlMessages id='validation.emailFormat' />,
                )
            : yup
                .string()
                .optional()
                .nullable()
                .max(100)
                .test(
                  'optional-email-format',
                  () => <IntlMessages id='validation.emailFormat' />,
                  (val) =>
                    !val ||
                    /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(val),
                ),
          coach_phone: coachPhoneRequired
            ? yup
                .string()
                .required(<IntlMessages id='validation.phoneNumberRequired' />)
                .matches(/^\d{3}-\d{3}-\d{4}$/, () => (
                  <IntlMessages id='validation.phoneNumberValid' />
                ))
            : yup
                .string()
                .optional()
                .nullable()
                .test(
                  'optional-phone-format',
                  () => <IntlMessages id='validation.phoneNumberValid' />,
                  (val) => !val || /^\d{3}-\d{3}-\d{4}$/.test(val),
                ),
          division_id: yup
            .number()
            .required(<IntlMessages id='validation.division.required' />),
        }),
      )
      .min(1, () => <IntlMessages id='validation.teams.min' />)
      .max(maxTeamAllowed, 'validation.teams.max'),
  });

const getControlStyles = (disabled) => ({
  color: disabled ? 'gray' : 'inherit',
  pointerEvents: disabled ? 'none' : 'auto',
  cursor: disabled ? 'not-allowed' : 'pointer',
});
const getColor = (companyTheme, defaultColor = '#2196f3') => {
  return companyTheme ? companyTheme.primary.main : defaultColor;
};
const getBackgroundColor = (companyTheme, value) => {
  return companyTheme
    ? alpha(companyTheme.primary.main, value)
    : alpha('#2196f3', value);
};

const TeamConfigure = ({ onNext, onBack, divisions, wizardSettings }) => {
  const [orgData, setOrgData] = useState([]);
  const [orgIdData, setOrgIdData] = useState();
  const [isSameExists, setIsSameExists] = useState({});
  const { tournamentSlug } = useParams();
  const { user } = useAuthUser();
  const [savedProgress, setSavedProgress] = useState(
    getSavedProgress(tournamentSlug, getAuthenticatedProgressUserId(user)),
  );
  const companyTheme = CompanyTheme();
  const userId = savedProgress?.data?.userId ?? user?.id;
  const infoViewActionsContext = useInfoViewActionsContext();
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [message, setMessage] = useState('');
  const editorRef = useRef(null);
  const [settings, setSettings] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [coachUserData, setCoachUserData] = useState(null);
  const [expandedTeams, setExpandedTeams] = useState({});

  const isMobile = useMediaQuery('(max-width:655px)');
clearTournamentWidgetReturnPath();
  const [selectedOrgOption, setSelectedOrgOption] = useState(
    savedProgress?.data?.teamConfig?.organization_name
      ? {
          id: savedProgress?.data?.teamConfig?.teams?.[0]?.org_id,
          organization_name: savedProgress?.data?.teamConfig?.organization_name,
        }
      : null,
  );
  const fetchOrganisationData = async () => {
    try {
      const response = await jwtAxios.get(`/tournament-org/${orgIdData}`);
      const { data } = response;
      setOrgData(data);
      fetchSuggestions(data?.organization_name);
      setSelectedOrgOption({
        id: data.id,
        organization_name: data.organization_name,
      });
    } catch (error) {
      console.error('Error Fetching Users Data:', error);
    }
  };
  useEffect(() => {
    if (orgIdData) {
      fetchOrganisationData();
    }
  }, [orgIdData]);
  const fetchSuggestions = async (inputValue) => {
    if (inputValue.trim() === '') {
      setSuggestions([]);
      return;
    }
    try {
      const response = await jwtAxios.get(`/tournament-org/`, {
        params: { search: inputValue },
      });
      const ReqData = await response?.data?.list?.map((field) => ({
        id: field.id,
        organization_name: field.organization_name,
      }));
      setSuggestions(ReqData ?? []);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
      return [];
    }
  };

  useEffect(() => {
    const savedProgress = getSavedProgress(
      tournamentSlug,
      getAuthenticatedProgressUserId(user),
    );
    if (savedProgress?.data?.orgId) {
      setOrgIdData(savedProgress?.data?.orgId);
    }
    if (savedProgress?.data?.signUp) {
      setCoachUserData(savedProgress?.data?.signUp);
    }
  }, []);
  const debouncedCheckTeamName = useDebounce(
    async (team_name, division_id, team_id) => {
      if (!team_name || !division_id) return;
      const key = `${team_name}_${division_id}`;

      try {
        const response = await jwtAxios.get(`/teams/${team_name}/${division_id}`);
        const existingTeamId = Number(response?.data?.team?.id);
        const currentTeamId = Number(team_id);
        const isSameTeam =
          Number.isFinite(existingTeamId) &&
          Number.isFinite(currentTeamId) &&
          existingTeamId === currentTeamId;
        if (response?.status === 200 && response.data?.team && !isSameTeam) {
          setIsSameExists((prev) => ({
            ...prev,
            [key]: true,
          }));
        } else {
          setIsSameExists((prev) => ({
            ...prev,
            [key]: false,
          }));
        }
      } catch (error) {
        console.error(error);
      }
    },
    500, // debounce delay in ms
  );

  useEffect(() => {
    fetchcompanyData();
  }, []);

  const fetchcompanyData = async () => {
    try {
      const response = await jwtAxios.get(
        user?.id ? `/app-settings/` : `/public/app-settings`,
      );
      setSettings(response?.data?.basic);
    } catch (error) {
      console.error('Error fetching appearance settings:', error);
    }
  };

  const handleSubmit = async () => {
    try {
      await jwtAxios.post(`/tournament-insecure/contact-support`, {
        email: settings?.site_contact_email,
        content: message,
        userDetails: {
          first_name: coachUserData?.first_name ?? user?.first_name,
          last_name: coachUserData?.last_name ?? user?.last_name,
          email: coachUserData?.email ?? user?.email,
        },
      });
      setMessage('');
      handleClose();
      infoViewActionsContext.showMessage('Email sent successfully');
    } catch (error) {
      infoViewActionsContext.fetchError(error.message);
    }
  };
  const teamLabel = wizardSettings?.team_label || 'Team';
  const organizationLabel =
    wizardSettings?.organization_label || 'Organization';
  const coachLabel = wizardSettings?.coach_label || 'Coach';
  const maxTeamAllowed = wizardSettings?.max_teams_allow ?? 6;

  const formInitialValues = useMemo(() => {
    const teamCfg = savedProgress?.data?.teamConfig || {};
    const uid = savedProgress?.data?.userId ?? user?.id;
    const savedOrgId = savedProgress?.data?.orgId ?? '';
    return {
      organization_name:
        teamCfg.organization_name || orgData?.organization_name || '',
      teams:
        teamCfg.teams?.length > 0
          ? teamCfg.teams.map((t) => ({
              ...t,
              team_name: t.team_name || '',
              coach_name: t.coach_name || '',
              coach_email: t.coach_email || '',
              coach_phone: t.coach_phone || '',
              user_id: t.user_id ?? uid,
              org_id: t.org_id ?? savedOrgId,
              division_id: t.division_id,
              division_name:
                t.division_name ||
                divisions?.find((d) => d.id === t.division_id)?.division_name ||
                '',
            }))
          : [
              {
                division_id: '',
                division_name: '',
                team_name: '',
                coach_name: '',
                coach_email: '',
                coach_phone: '',
                user_id: uid,
                org_id: savedOrgId,
              },
            ],
      role: 'coach',
    };
  }, [savedProgress, user?.id, divisions]);

  const validateCapacityBeforeSubmit = (teams = []) => {
    const divisionCounts = new Map();
    const existingCounts = new Map();
    const availabileTeams = teams || [];
    availabileTeams.forEach((team) => {
      const divisionId = Number(team?.division_id);
      if (!divisionId) return;
      divisionCounts.set(divisionId, (divisionCounts.get(divisionId) || 0) + 1);
      if (team?.team_id) {
        existingCounts.set(
          divisionId,
          (existingCounts.get(divisionId) || 0) + 1,
        );
      }
    });

    for (const [divisionId, requestedCount] of divisionCounts.entries()) {
      const divisionData = divisions?.find((d) => Number(d.id) === divisionId);
      if (!divisionData) {
        return 'Selected division not found.';
      }

      const remainingSpots = Number(divisionData.remaining_spots || 0);
      const existingInDivision = Number(existingCounts.get(divisionId) || 0);
      // Existing teams already occupy spots, so let users keep those while editing.
      const allowedCount = remainingSpots + existingInDivision;

      if (requestedCount > allowedCount) {
        return `Only ${allowedCount} team(s) can be assigned to "${divisionData.division_name}" right now.`;
      }
    }

    return null;
  };

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 2, sm: 3 },
        maxWidth: '100%',
        width: '100%',
        mx: 'auto',
      }}
    >
      <Typography
        variant='h2'
        sx={{ mb: 4, fontWeight: 'bold', textAlign: 'center' }}
      >
        Configure Your {teamLabel}s
      </Typography>
      <Container
        maxWidth='md'
        sx={{ mt: { xs: 4, sm: 6, md: 8 }, px: { xs: 0, sm: 2 } }}
      >
        <Formik
          initialValues={formInitialValues}
          enableReinitialize
          validationSchema={buildValidationSchema({
            coachFullNameRequired:
              wizardSettings?.coach_full_name_required ?? true,
            coachEmailRequired: wizardSettings?.coach_email_required ?? true,
            coachPhoneRequired: wizardSettings?.coach_phone_required ?? true,
            maxTeamAllowed: maxTeamAllowed,
            teamLabel: teamLabel,
            organizationLabel: organizationLabel,
            coachLabel: coachLabel,
          })}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              setSubmitting(true);
              const capacityError = validateCapacityBeforeSubmit(values?.teams);
              if (capacityError) {
                infoViewActionsContext.fetchError(capacityError);
                return;
              }
              // Persist full draft first so UI state is recoverable if API fails midway.
              saveProgress(
                tournamentSlug,
                1,
                {
                  teamConfig: values,
                  orgId: orgIdData || savedProgress?.data?.orgId,
                },
                user?.id || userId,
              );

              // Save or update organization first
              let orgResponse;
              if (orgIdData) {
                orgResponse = await jwtAxios.put(`/tournament-org/${orgIdData}`, {
                  organization_name: values.organization_name,
                });
              } else {
                orgResponse = await jwtAxios.post('/tournament-org', {
                  organization_name: values.organization_name,
                  user_id: userId,
                });
              }

              const orgId = orgResponse?.data?.id;
              setOrgIdData(orgId);

              // Prepare to save/update teams
              const teamIds = [];

              for (const [index, team] of values.teams.entries()) {
                const encrypteData = await encrypt('t-registration');
                const payload = {
                  org_id: orgId,
                  division_id: team.division_id,
                  team_name: team.team_name,
                  coach_name: team.coach_name,
                  coach_email: team.coach_email,
                  coach_phone: team.coach_phone,
                  user_id: userId,
                  isTeams: encrypteData,
                };

                let response;

                // Get existing team ID from saved progress, fallback to undefined
                const existingTeamId = savedProgress?.data?.teamId?.find(
                  (t) => t.team_id === team.team_id,
                )?.team_id;
                if (existingTeamId) {
                  // Update existing team
                  const { division_id, ...updatedPayload } = payload;
                  response = await jwtAxios.put(`/teams/${existingTeamId}`, updatedPayload);
                } else {
                  // Create new team
                  response = await jwtAxios.post('/teams', payload);
                }

                const team_id = response?.data?.id;
                if (!team_id) {
                  throw new Error(
                    `No team ID returned for team at index ${index}`,
                  );
                }
                values.teams[index].team_id = team_id;
                teamIds.push({ index, team_id });
              }

              // Refresh data
              orgIdData && fetchOrganisationData();

              // Save progress with team IDs and org ID
              saveProgress(
                tournamentSlug,
                1,
                {
                  teamConfig: values,
                  orgId: orgId,
                  teamId: teamIds,
                },
                user?.id || userId,
              );
              onNext(values);
              infoViewActionsContext.fetchSuccess(
                'Organization and teams saved successfully',
              );
            } catch (error) {
              infoViewActionsContext.fetchError(
                error?.response?.data?.error ||
                  'Failed to save team configuration',
              );
              console.error('Error submitting form:', error);
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {(formikProps) => {
            const {
              values,
              errors,
              touched,
              handleChange,
              setFieldValue,
              isSubmitting,
              submitCount,
            } = formikProps;

            const coachFullNameRequired =
              wizardSettings?.coach_full_name_required ?? true;
            const coachEmailRequired =
              wizardSettings?.coach_email_required ?? true;
            const coachPhoneRequired =
              wizardSettings?.coach_phone_required ?? true;

            const renderRequiredAsterisk = (required) =>
              required ? <span style={{ color: 'red' }}> *</span> : null;

            // Mark only later duplicate names (Team 6 duplicates Team 1 -> show on Team 6).
            const duplicateTeamNameErrors = {};
            const firstTeamNameIndex = new Map();
            (values?.teams || []).forEach((team, idx) => {
              const normalizedName = (team?.team_name || '')
                .trim()
                .toLowerCase();
              if (!normalizedName) return;
              if (firstTeamNameIndex.has(normalizedName)) {
                const firstIdx = firstTeamNameIndex.get(normalizedName);
                duplicateTeamNameErrors[
                  idx
                ] = `Same ${teamLabel} name exists on ${teamLabel} ${
                  firstIdx + 1
                }`;
                return;
              }
              firstTeamNameIndex.set(normalizedName, idx);
            });
            const getTeamCompletion = (team) => {
              const checks = [
                Boolean(team?.team_name),
                !coachFullNameRequired || Boolean(team?.coach_name),
                !coachEmailRequired || Boolean(team?.coach_email),
                !coachPhoneRequired || Boolean(team?.coach_phone),
                Boolean(team?.division_id),
              ];
              const completeCount = checks.filter(Boolean).length;
              return {
                completeCount,
                totalCount: checks.length,
                isComplete: completeCount === checks.length,
              };
            };

            const getTeamNameError = (team, index) => {
              const fieldLevelNameError =
                (touched.teams?.[index]?.team_name || submitCount > 0) &&
                errors.teams?.[index]?.team_name;
              const duplicateNameError = duplicateTeamNameErrors[index];
              const backendDuplicateError = isSameExists?.[
                `${team.team_name}_${team.division_id}`
              ]
                ? `${teamLabel} name with that name already exists in this division`
                : '';
              return (
                duplicateNameError ||
                fieldLevelNameError ||
                backendDuplicateError
              );
            };

            const handleTeamNameChange = (e, team) => {
              // Clear stale duplicate flags; fresh checks run for edited row.
              setIsSameExists({});
              handleChange(e);
              debouncedCheckTeamName(
                e.target.value,
                team.division_id,
                team.team_id || team.id,
              );
            };

            const handleTeamNameBlur = (team) => {
              if (!team.team_name || !team.division_id) {
                setIsSameExists((prev) => ({
                  ...prev,
                  [`${team.team_name}_${team.division_id}`]: false,
                }));
              }
            };

            const handleDivisionChange = (e, team, index) => {
              setIsSameExists({});
              handleChange(e);
              const division = divisions?.find((d) => d.id === e.target.value);
              setFieldValue(
                `teams[${index}].division_name`,
                division?.division_name || '',
              );
              debouncedCheckTeamName(
                team.team_name,
                e.target.value,
                team.team_id || team.id,
              );
            };

            // Adjust number of teams for this registration.
            const handleSetTeamCount = async (nextCount) => {
              const clamped = Math.max(1, Math.min(maxTeamAllowed, nextCount));
              const current = values?.teams?.length || 0;
              if (clamped === current) return;

              // Tail removal only (keeps indices stable for the remaining teams).
              if (clamped < current) {
                const removedTeams = values.teams.slice(clamped);
                for (const t of removedTeams) {
                  if (t?.team_id) {
                    try {
                      await jwtAxios.delete(`/teams/${t.team_id}`);
                    } catch (err) {
                      console.error('Error deleting team:', err);
                    }
                  }
                }

                const newTeams = values.teams.slice(0, clamped);
                setFieldValue('teams', newTeams);

                const removedTeamIds = removedTeams
                  .map((t) => t?.team_id)
                  .filter(Boolean);
                const updatedTeamIds = savedProgress?.data?.teamId?.filter(
                  (t) => !removedTeamIds.includes(t?.team_id),
                );

                saveProgress(
                  tournamentSlug,
                  2,
                  {
                    teamConfig: {
                      ...values,
                      teams: newTeams,
                    },
                    teamId: updatedTeamIds,
                  },
                  userId,
                );
                setOrgData({ organization_name: values.organization_name });
                return;
              }

              // Increasing teams: add blank teams to the end.
              if (clamped > current) {
                const teamsToAdd = clamped - current;
                const addedTeams = Array.from({ length: teamsToAdd }, () => ({
                  division_id: '',
                  division_name: '',
                  team_name: '',
                  coach_name: '',
                  coach_email: '',
                  coach_phone: '',
                  user_id: userId,
                  org_id: orgIdData,
                }));
                const newTeams = [...values.teams, ...addedTeams];
                setFieldValue('teams', newTeams);
                saveProgress(
                  tournamentSlug,
                  2,
                  {
                    teamConfig: {
                      ...values,
                      teams: newTeams,
                    },
                  },
                  userId,
                );
              }
            };

            // Add a team (no division pre-selected)
            const handleAddTeam = () =>
              values?.teams?.length >= maxTeamAllowed
                ? null
                : handleSetTeamCount((values?.teams?.length || 0) + 1);

            const handleCopyCoachFromFirst = (index) => {
              if (index <= 0 || !values?.teams?.[0]) return;
              setFieldValue(
                `teams[${index}].coach_name`,
                values.teams[0].coach_name || '',
              );
              setFieldValue(
                `teams[${index}].coach_email`,
                values.teams[0].coach_email || '',
              );
              setFieldValue(
                `teams[${index}].coach_phone`,
                values.teams[0].coach_phone || '',
              );
            };

            const handleRemoveTeam = async (index) => {
              const teamToRemove = values?.teams?.[index];
              const teamId = teamToRemove?.team_id;

              if (teamId) {
                try {
                  await jwtAxios.delete(`/teams/${teamId}`);
                } catch (err) {
                  console.error('Error deleting team:', err);
                }
              }

              const newTeams = values.teams.filter((_, i) => i !== index);
              setFieldValue('teams', newTeams);

              const updatedTeamIds = await savedProgress?.data?.teamId?.filter(
                (t) => t?.team_id !== teamId,
              );
              saveProgress(
                tournamentSlug,
                2,
                {
                  teamConfig: {
                    ...values,
                    teams: newTeams,
                  },
                  teamId: updatedTeamIds,
                },
                userId,
              );

              setOrgData({ organization_name: values.organization_name });
            };

            const toggleTeamExpanded = (index) => {
              setExpandedTeams((prev) => ({
                ...prev,
                [index]: !(prev[index] ?? true),
              }));
            };

            // Show all active divisions for every team row.
            const getAvailableDivisions = () =>
              divisions?.filter(
                (division) => division?.purchase_option_count > 0,
              );
            const teamCount = values?.teams?.length || 0;

            const isMinReached = teamCount <= 1;
            const isMaxReached = teamCount >= maxTeamAllowed;
            return (
              <Form noValidate autoComplete='off'>
                <AppGridContainer sx={{ mt: 2 }}>
                  <Grid size={{ xs: 12, md: 12 }}>
                    <Box
                      sx={{
                        border: '1px solid #EAECF0',
                        p: { xs: 2, sm: 4, md: 6 },
                        borderRadius: 3,
                        boxShadow:
                          '0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)',
                      }}
                    >
                      <AppGridContainer>
                        <Grid size={{ xs: 12, md: 12 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              width: '100%',
                              mb: 5,
                            }}
                          >
                            <Typography variant='h2' fontWeight='bold'>
                              {organizationLabel}
                            </Typography>
                          </Box>
                          <Autocomplete
                            options={suggestions.toSorted((a, b) =>
                              a.organization_name?.localeCompare(
                                b.organization_name,
                                undefined,
                                {
                                  numeric: true,
                                  sensitivity: 'base',
                                },
                              ),
                            )}
                            getOptionLabel={(option) =>
                              typeof option === 'string'
                                ? option
                                : option.organization_name
                            }
                            value={selectedOrgOption}
                            onChange={(e, selectedOption) => {
                              setSelectedOrgOption(selectedOption || null);
                              setFieldValue(
                                'organization_name',
                                selectedOption?.organization_name || '',
                              );
                              setOrgIdData(selectedOption?.id || null);
                              if (!selectedOption) {
                                setOrgData(null);
                                setSuggestions([]);
                              }
                            }}
                            onInputChange={(e, inputValue, reason) => {
                              if (reason === 'input') {
                                setFieldValue('organization_name', inputValue);
                                fetchSuggestions(inputValue);
                                setSelectedOrgOption(null);
                                setOrgIdData(null);
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={
                                  <span>
                                    {`Type to search for existing ${organizationLabel}  name or create new`}
                                    {renderRequiredAsterisk(true)}
                                  </span>
                                }
                                fullWidth
                                error={
                                  touched.organization_name &&
                                  !!errors.organization_name
                                }
                                helperText={
                                  touched.organization_name &&
                                  errors.organization_name
                                }
                              />
                            )}
                            freeSolo
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 12 }}>
                          <Typography
                            variant='h2'
                            sx={{ fontWeight: 700, mb: 2 }}
                          >
                            {teamLabel} Details
                          </Typography>

                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: isMobile
                                ? 'flex-end'
                                : 'space-between',
                              gap: 0.5,
                              border: '1px solid #EAECF0',
                              borderRadius: 3,
                              p: { xs: 2, sm: 2.5, md: 3 },
                              boxShadow:
                                '0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)',
                              mb: 3,
                              ...(isMobile && { flexWrap: 'wrap' }),
                            }}
                          >
                            <Box>
                              <Typography
                                variant='subtitle1'
                                fontWeight='bold'
                                sx={{ mb: 0.5 }}
                              >
                                Number of {teamLabel}
                              </Typography>
                              <Typography
                                variant='body2'
                                color='text.secondary'
                                sx={{ mb: 1.5 }}
                              >
                                You can register between 1 and {maxTeamAllowed}{' '}
                                {teamLabel}s per organization.
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                gap: 0.5,
                              }}
                            >
                              <Box
                                border={1}
                                borderRadius={4}
                                display='inline-flex'
                                borderColor='text.secondary'
                                alignItems='center'
                                justifyContent='center'
                                width={100}
                                height={36}
                              >
                                <RemoveIcon
                                  onClick={() =>
                                    handleSetTeamCount(teamCount - 1)
                                  }
                                  sx={getControlStyles(isMinReached)}
                                />

                                <Box component='span' px={3}>
                                  {teamCount}
                                </Box>

                                <AddIcon
                                  onClick={() =>
                                    handleSetTeamCount(teamCount + 1)
                                  }
                                  sx={getControlStyles(isMaxReached)}
                                />
                              </Box>
                            </Box>
                          </Box>
                        </Grid>
                        {values?.teams?.map((team, index) => {
                          const completion = getTeamCompletion(team);
                          const isExpanded = expandedTeams[index] ?? true;
                          return (
                            <Grid
                              size={{ xs: 12, md: 12 }}
                              key={`team-card-${index + 1}`}
                            >
                              <Box
                                sx={{
                                  border: '1px solid #E5E7EB',
                                  borderRadius: 3,
                                  overflow: 'hidden',
                                  mb: 3,
                                  backgroundColor: '#fff',
                                }}
                              >
                                <Box
                                  sx={{
                                    backgroundColor: `#F8FAFC`,
                                    color: '#111827',
                                    px: 2,
                                    py: 1.25,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    borderBottom: '1px solid #E5E7EB',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        minWidth: 22,
                                        height: 22,
                                        borderRadius: '4px',
                                        border: '1px solid #D1D5DB',
                                        color: `#374151`,
                                        display: 'flex',
                                        alignItems: 'flex-end',
                                        justifyContent: 'center',
                                        fontSize: 12,
                                        fontWeight: 700,
                                      }}
                                    >
                                      {index + 1}
                                    </Box>
                                    <Box sx={{ fontWeight: 700 }}>
                                      {`${teamLabel} ${index + 1}`}
                                    </Box>
                                    <Box
                                      sx={{
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: 1,
                                        fontSize: 11,
                                        fontWeight: 700,
                                        color: !completion.isComplete
                                          ? '#B45309'
                                          : '#fefffe',
                                        backgroundColor: !completion.isComplete
                                          ? '#FEF3C7'
                                          : '#7cf74f',
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.5,
                                      }}
                                    >
                                      {!completion.isComplete
                                        ? 'Incomplete'
                                        : 'Completed'}
                                    </Box>
                                  </Box>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                    }}
                                  >
                                    {(values?.teams?.length || 0) > 1 && (
                                      <IconButton
                                        size='small'
                                        color='error'
                                        sx={{
                                          borderColor: completion.isComplete
                                            ? '#d32f2f'
                                            : 'none',
                                          opacity: completion.isComplete
                                            ? 0.5
                                            : 1,
                                        }}
                                        onClick={() => handleRemoveTeam(index)}
                                      >
                                        <DeleteOutlineIcon fontSize='small' />
                                      </IconButton>
                                    )}
                                    <IconButton
                                      size='small'
                                      sx={{
                                        color: `${
                                          !completion.isComplete
                                            ? '#374151'
                                            : '#F8FAFC'
                                        }`,
                                      }}
                                      onClick={() => toggleTeamExpanded(index)}
                                    >
                                      <KeyboardArrowUpIcon
                                        fontSize='small'
                                        sx={{
                                          transform: isExpanded
                                            ? 'rotate(0deg)'
                                            : 'rotate(180deg)',
                                          transition: 'transform 0.2s ease',
                                        }}
                                      />
                                    </IconButton>
                                  </Box>
                                </Box>
                                {isExpanded && (
                                  <AppGridContainer
                                    sx={{ p: 2, rowGap: '15px' }}
                                  >
                                    <Grid size={{ xs: 12, md: 6 }}>
                                      <TextField
                                        fullWidth
                                        name={`teams[${index}].team_name`}
                                        label={
                                          <span>
                                            {`${teamLabel} Name`}
                                            {renderRequiredAsterisk(true)}
                                          </span>
                                        }
                                        value={team.team_name}
                                        onChange={(e) =>
                                          handleTeamNameChange(e, team)
                                        }
                                        onBlur={() => handleTeamNameBlur(team)}
                                        error={Boolean(
                                          getTeamNameError(team, index),
                                        )}
                                        helperText={getTeamNameError(
                                          team,
                                          index,
                                        )}
                                        variant='outlined'
                                        sx={{ mb: 1 }}
                                      />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                      <TextField
                                        select
                                        fullWidth
                                        name={`teams[${index}].division_id`}
                                        label={
                                          <span>
                                            {`Division`}
                                            <span style={{ color: 'red' }}>
                                              {' '}
                                              *
                                            </span>
                                          </span>
                                        }
                                        value={team.division_id}
                                        onChange={(e) =>
                                          handleDivisionChange(e, team, index)
                                        }
                                        error={
                                          touched.teams?.[index]?.division_id &&
                                          Boolean(
                                            errors.teams?.[index]?.division_id,
                                          )
                                        }
                                        helperText={
                                          touched.teams?.[index]?.division_id &&
                                          errors.teams?.[index]?.division_id
                                        }
                                        variant='outlined'
                                        sx={{ mb: 1 }}
                                      >
                                        <MenuItem value=''>
                                          <em>Select Division</em>
                                        </MenuItem>
                                        {getAvailableDivisions()?.map(
                                          (division) => (
                                            <MenuItem
                                              key={division.id}
                                              value={division.id}
                                              disabled={
                                                division.availability_status ===
                                                  'full' &&
                                                team.division_id !== division.id
                                              }
                                            >
                                              {`${division.division_name} (${
                                                division.remaining_spots || 0
                                              } spots left${
                                                division.availability_status ===
                                                'full'
                                                  ? ' - Full'
                                                  : ''
                                              })`}
                                            </MenuItem>
                                          ),
                                        )}
                                      </TextField>
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 12 }}>
                                      <Box
                                        sx={{
                                          mt: 0.5,
                                          mb: 0.5,
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                        }}
                                      >
                                        <Typography
                                          variant='subtitle2'
                                          sx={{ fontWeight: 700 }}
                                        >
                                          {coachLabel} Information
                                        </Typography>
                                        {index > 0 && (
                                          <Button
                                            size='small'
                                            onClick={() =>
                                              handleCopyCoachFromFirst(index)
                                            }
                                            sx={{
                                              textTransform: 'none',
                                              fontSize: 12,
                                              minWidth: 'auto',
                                              px: 0,
                                            }}
                                          >
                                            Copy from {teamLabel} 1
                                          </Button>
                                        )}
                                      </Box>
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                      <TextField
                                        fullWidth
                                        name={`teams[${index}].coach_name`}
                                        label={
                                          <span>
                                            {`${coachLabel} Full Name`}
                                            {renderRequiredAsterisk(
                                              coachFullNameRequired,
                                            )}
                                          </span>
                                        }
                                        value={team.coach_name}
                                        onChange={handleChange}
                                        error={
                                          touched.teams?.[index]?.coach_name &&
                                          Boolean(
                                            errors.teams?.[index]?.coach_name,
                                          )
                                        }
                                        helperText={
                                          touched.teams?.[index]?.coach_name &&
                                          errors.teams?.[index]?.coach_name
                                        }
                                        variant='outlined'
                                        sx={{ mb: 2 }}
                                      />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                      <TextField
                                        fullWidth
                                        name={`teams[${index}].coach_phone`}
                                        label={
                                          <span>
                                            {`${coachLabel} Phone Number`}
                                            {renderRequiredAsterisk(
                                              coachPhoneRequired,
                                            )}
                                          </span>
                                        }
                                        value={team.coach_phone}
                                        type='tel'
                                        onChange={(event) => {
                                          const formatted =
                                            HandleMobileFormat(event);
                                          setFieldValue(
                                            `teams[${index}].coach_phone`,
                                            formatted,
                                          );
                                        }}
                                        error={
                                          touched.teams?.[index]?.coach_phone &&
                                          Boolean(
                                            errors.teams?.[index]?.coach_phone,
                                          )
                                        }
                                        helperText={
                                          touched.teams?.[index]?.coach_phone &&
                                          errors.teams?.[index]?.coach_phone
                                        }
                                        variant='outlined'
                                        sx={{ mb: 2 }}
                                      />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 12 }}>
                                      <TextField
                                        fullWidth
                                        name={`teams[${index}].coach_email`}
                                        label={
                                          <span>
                                            {`${coachLabel} Email`}
                                            {renderRequiredAsterisk(
                                              coachEmailRequired,
                                            )}
                                          </span>
                                        }
                                        value={team.coach_email}
                                        onChange={(e) => {
                                          handleChange(e);
                                        }}
                                        error={
                                          touched.teams?.[index]?.coach_email &&
                                          Boolean(
                                            errors.teams?.[index]?.coach_email,
                                          )
                                        }
                                        helperText={
                                          touched.teams?.[index]?.coach_email &&
                                          errors.teams?.[index]?.coach_email
                                        }
                                        variant='outlined'
                                        sx={{ mb: 0.5 }}
                                      />
                                    </Grid>
                                  </AppGridContainer>
                                )}
                              </Box>
                            </Grid>
                          );
                        })}
                        <Grid size={{ xs: 12, md: 12 }}>
                          <Typography
                            sx={{ color: 'red', textAlign: 'center', mt: 1 }}
                          >
                            {typeof errors.teams === 'string' && (
                              <IntlMessages id={errors.teams} />
                            )}
                          </Typography>
                        </Grid>
                      </AppGridContainer>
                      <Box
                        sx={{
                          border: '1px dashed #ccc',
                          borderRadius: 2,
                          padding: 1,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          width: '100%',
                          marginTop: 2,
                        }}
                      >
                        <Button
                          onClick={handleAddTeam}
                          startIcon={<AddIcon />}
                          disabled={
                            (values?.teams?.length || 0) >= maxTeamAllowed
                          }
                          sx={{
                            color: '#6b7280', // Tailwind's gray-500-like color
                            textTransform: 'none',
                            fontWeight: 500,
                          }}
                        >
                          Add Another {teamLabel}
                        </Button>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, md: 12 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: user?.id ? 'flex-end' : 'space-between',
                        mt: 4,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      {!user?.id && (
                        <Button
                          onClick={onBack}
                          variant='contained'
                          sx={{
                            minWidth: 100,
                            backgroundColor: getColor(companyTheme),
                            '&:hover': {
                              backgroundColor: companyTheme
                                ? companyTheme.primary.dark
                                : '#1976d2',
                            },
                          }}
                        >
                          Back
                        </Button>
                      )}
                      <Button
                        type='submit'
                        variant='contained'
                        disabled={isSubmitting}
                        sx={{
                          minWidth: 100,
                          backgroundColor: getColor(companyTheme),
                          '& .MuiButton-startIcon': {
                            marginRight: '4px',
                          },
                          '&:hover': {
                            backgroundColor: companyTheme
                              ? companyTheme.primary.dark
                              : '#1976d2',
                          },
                        }}
                      >
                        Next
                      </Button>
                    </Box>
                  </Grid>
                </AppGridContainer>
              </Form>
            );
          }}
        </Formik>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 8,
            textAlign: 'center',
          }}
        >
          <Typography variant='body2' color='text.secondary'>
            Need help ?{' '}
            <Link
              href='#'
              underline='hover'
              color='primary'
              onClick={handleOpen}
            >
              Contact support
            </Link>
          </Typography>
        </Box>
        <AppInfoView />
      </Container>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Contact Support</DialogTitle>
        <DialogContent>
          <Grid
            size={{ xs: 12 }}
            style={{ marginBottom: '10px', marginTop: '20px' }}
          >
            <TextField
              label='Email'
              name='email'
              type='email'
              fullWidth
              variant='outlined'
              value={settings?.site_contact_email ?? settings?.email}
              disabled
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography
              variant='h4'
              style={{ marginBottom: '10px', marginTop: '10px' }}
            >
              Message
            </Typography>
            <ReactQuill
              className='desc-box'
              ref={editorRef}
              value={message}
              onChange={setMessage}
              modules={{
                toolbar: [
                  [{ header: '1' }, { header: '2' }, { font: [] }],
                  [{ list: 'ordered' }, { list: 'bullet' }],
                  ['bold', 'italic', 'underline', 'strike'],
                  [{ align: [] }],
                  ['link'],
                ],
              }}
              formats={[
                'header',
                'font',
                'list',
                'bold',
                'italic',
                'underline',
                'strike',
                'align',
                'link',
              ]}
            />
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type='submit' onClick={handleSubmit} disabled={!message}>
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

TeamConfigure.propTypes = {
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  divisions: PropTypes.array.isRequired,
  wizardSettings: PropTypes.shape({
    organization_label: PropTypes.string,
    team_label: PropTypes.string,
    coach_label: PropTypes.string,
    coach_full_name_required: PropTypes.bool,
    coach_email_required: PropTypes.bool,
    coach_phone_required: PropTypes.bool,
    max_teams_allow: PropTypes.any,
  }),
};

export default TeamConfigure;
